// ContactInformation.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { AvailabilityButtonSection } from './sections/AvailabilityButtonSection';
import { AvailabilitySelectionSection } from './sections/AvailabilitySelectionSection';
import { TimezoneSelectionSection } from './sections/TimezoneSelectionSection/TimezoneSelectionSection';
import { Box } from '@mui/material';
import { useSurveyContent } from '../../../../../shared/hooks';
import moment from 'moment-timezone';
import {
  useHandleSubmit,
  useAvailabilityData,
} from '../../../../apps/consultations_refresh/Availability/hooks';
import Alert from '../../../alert';
import { useFinalizeCustomBlock } from '../../../../apps/consultations_refresh/ContactInformation/hooks';

export interface TimeSlot {
  time: string;
  selected: boolean;
  disabled: boolean;
}

export interface DayData {
  shortDay: string;
  date: string;
  timeSlots: TimeSlot[];
  isoDate: string;
}

type FetchedAvailability = {
  date: string;
  time: string;
  timezone: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const formatTimeEn = (d: Date) =>
  d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export function generateInitialDays(
  count: number,
  startDate: string | undefined,
  startHour: string,
  endHour: string,
  offsetMinutes = 0,
  displayNow?: Date, // must be "now" in the SELECTED timezone
): DayData[] {
  // 1) Base start day (source timezone), normalized to local midnight
  let baseStart = new Date();
  if (startDate) {
    const [y, m, d] = startDate.split('-').map(Number);
    baseStart = new Date(y, m - 1, d);
  }
  baseStart = toMidnight(baseStart);

  // 2) Convert the start window into the DISPLAY timezone (via minute offset)
  const displayStartInitial = toMidnight(new Date(baseStart.getTime() + offsetMinutes * 60 * 1000));

  // 3) If we have a "now" in DISPLAY timezone, shift the visible window forward
  //    so the first day shown is today (or later if start was in the past).
  let displayStart = displayStartInitial;
  let effCount = count;
  if (displayNow) {
    const displayToday = toMidnight(displayNow);
    const shiftDays = Math.max(
      0,
      Math.floor((displayToday.getTime() - displayStartInitial.getTime()) / DAY_MS),
    );
    if (shiftDays > 0) {
      displayStart = new Date(displayStartInitial.getTime() + shiftDays * DAY_MS);
      effCount = Math.max(0, count - shiftDays);
    }
  }

  // 4) Prebuild the visible days (skip weekends)
  const days: DayData[] = [];
  for (let i = 0; i < effCount; i++) {
    const displayDate = new Date(displayStart.getTime() + i * DAY_MS);
    const dayOfWeek = displayDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue; // skip Sun/Sat
    const shortDay = displayDate.toLocaleDateString('en-US', { weekday: 'short' });
    const dateLabel = displayDate.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    });
    const yyyy = displayDate.getFullYear();
    const mm = String(displayDate.getMonth() + 1).padStart(2, '0');
    const dd = String(displayDate.getDate()).padStart(2, '0');
    days.push({
      shortDay: `${shortDay},`,
      date: dateLabel,
      timeSlots: [],
      isoDate: `${yyyy}-${mm}-${dd}`,
    });
  }

  // 5) Parse the hour window and detect rollover
  const [startH, startM] = startHour.split(':').map((n) => parseInt(n, 10));
  const [endH, endM] = endHour.split(':').map((n) => parseInt(n, 10));
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;
  const rollsOver = endMin <= startMin;

  // Build a lookup map from isoDate to the days array entry
  const daysByIso = new Map(days.map((d) => [d.isoDate, d]));

  // Helper: add a slot to the correct visible day (after applying offset)
  const addSlot = (baseDayOffset: number, hour: number, minute: number) => {
    const baseDateTime = new Date(baseStart.getTime() + baseDayOffset * DAY_MS);
    baseDateTime.setSeconds(0, 0);
    baseDateTime.setHours(hour, minute, 0, 0);

    // Apply offset to get DISPLAY timezone instant
    const displayDateTime = new Date(baseDateTime.getTime() + offsetMinutes * 60 * 1000);

    // If we have a "now" and this slot is in the past, HIDE it (do not push)
    if (displayNow && displayDateTime < displayNow) return;

    const displayMidnight = toMidnight(displayDateTime);
    const yyyy = displayMidnight.getFullYear();
    const mm = String(displayMidnight.getMonth() + 1).padStart(2, '0');
    const dd = String(displayMidnight.getDate()).padStart(2, '0');
    const iso = `${yyyy}-${mm}-${dd}`;
    const day = daysByIso.get(iso);
    if (!day) return;

    day.timeSlots.push({
      time: formatTimeEn(displayDateTime),
      selected: false,
      disabled: false, // not rendered if in the past, so no need to disable
    });
  };

  const addSlotByMinutes = (baseDayOffset: number, mins: number) => {
    const hour = Math.floor(mins / 60);
    const minute = mins % 60;
    addSlot(baseDayOffset, hour, minute);
  };

  const emitRange = (dayOffset: number, fromMin: number, toMinExclusive: number) => {
    for (let m = fromMin; m < toMinExclusive; m += 60) {
      addSlotByMinutes(dayOffset, m);
    }
  };

  const emitRangeInclusive = (dayOffset: number, fromMin: number, toMinInclusive: number) => {
    // reuse the exclusive loop by adding 1 minute
    emitRange(dayOffset, fromMin, toMinInclusive + 1);
  };

  // 6) Emit all base slots; grouping is determined by the display-day index
  for (let i = 0; i < count; i++) {
    if (!rollsOver) {
      // same-day window
      emitRangeInclusive(i, startMin, endMin); // end inclusive
    } else {
      // spans midnight: [start, 24:00) today, then [00:00, end) next day
      emitRange(i, startMin, 24 * 60);
      emitRangeInclusive(i + 1, 0, endMin);
    }
  }

  return days;
}

const getDeviceTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone.toLowerCase();
interface AvailabilityRule {
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  timezone?: string;
}

export const ContactInformation = (): JSX.Element => {
  const { surveyContent } = useSurveyContent();
  const getAvailability = useAvailabilityData();
  const handleSubmit = useHandleSubmit();
  const hasAvailability = surveyContent?.collect_availability;
  const surveyDuration = surveyContent?.content?.survey_duration ?? '';
  const availability = surveyContent?.availability_rule as AvailabilityRule | undefined;
  const redirect_to = surveyContent?.redirect_to;
  const startDate = availability?.start_date;
  const endDate = availability?.end_date;
  const startTime = availability?.start_time;
  const endTime = availability?.end_time;
  const loadedTimezone = availability?.timezone;
  const numDays = new Date(endDate as string).getTime() - new Date(startDate as string).getTime();
  const daysAux = Math.ceil(numDays / (1000 * 60 * 60 * 24)) + 1;
  const [selectedTimezone, setSelectedTimezone] = useState(getDeviceTimezone());
  const [currentPage, setCurrentPage] = useState(0);
  const [daysPerPage, setDaysPerPage] = useState(3);
  const [days, setDays] = useState<DayData[]>([]);
  const handleFinishCustomBlock = useFinalizeCustomBlock();
  const [showAlert, setShowAlert] = useState(false);
  const [finishedPosting, setFinishedPosting] = useState(false);
  const [serverAvailabilities, setServerAvailabilities] = useState<FetchedAvailability[]>([]);
  useEffect(() => {
    if (hasAvailability === false) {
      setFinishedPosting(true);
    }
  }, [hasAvailability]);
  const getCurrentAvailability = async (id: string) => {
    // pass an error setter if your hook expects it
    const res = await getAvailability(id);
    const fetchedAvailabilities = res?.availabilities ?? [];

    // If API returned a timezone, prefer it for the UI
    const fetchedTz = fetchedAvailabilities[0]?.timezone;
    if (fetchedTz) {
      setSelectedTimezone(fetchedTz);
    }
    setServerAvailabilities(fetchedAvailabilities);
  };
  useEffect(() => {
    if (surveyContent?.subject) {
      getCurrentAvailability(surveyContent?.subject);
    }
  }, [surveyContent?.subject]);

  useEffect(() => {
    if (!loadedTimezone || !selectedTimezone) return;

    const ref = startDate ?? moment().format('YYYY-MM-DD');
    const fromOffset = moment.tz(ref, 'YYYY-MM-DD', loadedTimezone).utcOffset();
    const toOffset = moment.tz(ref, 'YYYY-MM-DD', selectedTimezone).utcOffset();
    const tzOffsetMinutes = toOffset - fromOffset;

    // "Now" in the selected timezone
    const displayNow = moment.tz(selectedTimezone).toDate(); // NEW

    const initial = generateInitialDays(
      daysAux,
      startDate,
      startTime ?? '00:00:00',
      endTime ?? '23:00:00',
      tzOffsetMinutes,
      displayNow, // NEW
    );

    // clone and mark server selections (unchanged) ...
    const marked = initial.map((d) => ({ ...d, timeSlots: d.timeSlots.map((s) => ({ ...s })) }));

    const dayIndexByLabel = new Map(marked.map((d, i) => [d.date, i]));
    for (const fa of serverAvailabilities) {
      const src = moment.tz(`${fa.date} ${fa.time}`, 'YYYY-MM-DD HH:mm:ss', fa.timezone);
      const disp = src.clone().tz(selectedTimezone);
      const dayLabel = disp.format('MMM DD');
      const timeLabel = disp.format('h:mm A');

      const dayIdx = dayIndexByLabel.get(dayLabel);
      if (dayIdx === undefined) continue;

      const slotIdx = marked[dayIdx].timeSlots.findIndex((s) => s.time === timeLabel);
      if (slotIdx !== -1) {
        marked[dayIdx].timeSlots[slotIdx].selected = true;
      }
    }

    const visibleDays = marked.filter((d) => d.timeSlots.length > 0);
    setDays(visibleDays);
  }, [
    loadedTimezone,
    selectedTimezone,
    startDate,
    startTime,
    endTime,
    daysAux,
    serverAvailabilities,
  ]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(days.length / daysPerPage));
    if (currentPage > pages - 1) {
      setCurrentPage(pages - 1);
    }
  }, [days, daysPerPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [daysPerPage]);

  const toggleTimeSlot = (dayIndex: number, slotIndex: number) => {
    setDays((prevDays) =>
      prevDays.map((day, dIdx) =>
        dIdx === dayIndex
          ? {
              ...day,
              timeSlots: day.timeSlots.map((slot, sIdx) =>
                sIdx === slotIndex ? { ...slot, selected: !slot.selected } : slot,
              ),
            }
          : day,
      ),
    );
  };

  const submitAvailability = async () => {
    const selectedAvailabilities = days.filter((day) =>
      day.timeSlots.find((slot) => slot.selected),
    );
    const selectedAvailabilitiesFormatted = selectedAvailabilities.map((item) => {
      // Parse the date as local, avoid UTC shift
      const currentYear = new Date().getFullYear();
      const d = new Date(item.date);

      // Build YYYY-MM-DD using local parts
      const year = currentYear;
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      return {
        date: item.isoDate,
        times: item.timeSlots.filter((slot) => slot.selected).map((slot) => slot.time),
      };
    });
    const fullUrl = window.location.href;
    await handleFinishCustomBlock(fullUrl, true);
    await handleSubmit(
      {
        id: surveyContent?.subject ?? '',
        timezone: selectedTimezone,
        selected_availabilities: selectedAvailabilitiesFormatted,
      },
      setShowAlert,
      setFinishedPosting,
    );
  };

  useEffect(() => {
    if (finishedPosting) {
      const urlAux = window.location.href.replace('/a/', '/i/');
      if (!redirect_to) {
        window.location.assign(urlAux);
      } else {
        const newRedirectTo = redirect_to.replace('localhost', 'localhost:3000');
        window.location.assign(newRedirectTo);
      }
    }
  }, [finishedPosting]);

  const getTotalSelectedSlots = () => {
    return days.reduce(
      (total, day) => total + day.timeSlots.filter((slot) => slot.selected).length,
      0,
    );
  };

  const totalPages = Math.ceil(days.length / daysPerPage);
  const currentDays = days.slice(
    currentPage * daysPerPage,
    Math.min((currentPage + 1) * daysPerPage, days.length),
  );
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const totalAvailableSlots = days.reduce(
    (sum, d) => sum + d.timeSlots.filter((s) => !s.disabled).length,
    0,
  );

  // If there are fewer than 5 available slots, only require 1
  const minRequired = totalAvailableSlots < 5 ? 1 : 5;

  return (
    <Box display="flex" flexDirection="column" width="100%" minHeight="100svh" bgcolor="grey.50">
      {showAlert && (
        <Box sx={{ position: 'fixed', top: '16px', zIndex: 999999 }}>
          <Alert
            severity="error"
            open={showAlert}
            onClose={() => setShowAlert(false)}
            title="Sorry, there was an error submitting your availability"
            message="Please try again."
          />
        </Box>
      )}
      <Box component="main" display="flex" flexDirection="column" width="100%" gap={'8px'} pb={8}>
        <AvailabilitySelectionSection
          selectedTimezone={selectedTimezone}
          onTimezoneChange={setSelectedTimezone}
          surveyDuration={surveyDuration as string}
        />
        <TimezoneSelectionSection
          days={currentDays}
          onTimeSlotToggle={toggleTimeSlot}
          currentPage={currentPage}
          onNextPage={goToNextPage}
          onPrevPage={goToPrevPage}
          daysPerPage={daysPerPage}
          totalDays={days.length}
        />
        <AvailabilityButtonSection
          submitAvailability={submitAvailability}
          selectedCount={getTotalSelectedSlots()}
          minRequired={minRequired}
        />
      </Box>
    </Box>
  );
};
